import qs from "query-string";

export type MembersSort = "-user.profile.name";

export type OrganizationSort =
	| "meta.tsModified"
	| "meta.name"
	| "meta.tsCreated"
	| "-meta.tsModified"
	| "-meta.name"
	| "-meta.tsCreated";

export type PageSortOption = OrganizationSort | MembersSort;

export default interface PageQuery {
	limit: number;
	page: number;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	filters?: any;

	// Eg. search:{text:'Matteo', fields:['meta.name', 'meta.description']}
	search: {
		text: string;
		fields: string[];
	};

	sort: PageSortOption;

	// Projects gallery: only projects created by the current user ("me"), or
	// whose creator is an admin (incl. owner), senior or junior of the
	// organization. Absent means all.
	createdBy?: ProjectCreatedBy;
}

export type ProjectCreatedBy = "me" | "admin" | "senior" | "junior";

/**
 * Combine query-string with custom logic to parse `PageQuery` to URL string
 * @example - Returns string like: limit=10&page=1&search[text]=Project&search[fields]=meta.name&sort=meta.name
 */
export const getQueryStringFromPageQuery = (pq: PageQuery): string => {
	const { search, filters = {}, ...rest } = pq;

	const pqString = qs.stringify({ ...rest });
	const searchFields = search.fields
		.map((f) => `search[fields]=${f}`)
		.join("&");

	// The search text is free-form user/catalog input (material and object names
	// contain "&", "%", "#", "+", …), and this string is concatenated into the URL
	// by hand rather than going through qs.stringify — so it has to be encoded
	// here or those characters silently truncate or corrupt the query.
	const searchString =
		search.text.length && searchFields.length
			? `&search[text]=${encodeURIComponent(search.text)}&${searchFields}`
			: "";

	const filterFields = Object.keys(filters)
		.filter((field) => {
			const value = filters[field];
			// Filter out undefined, null, empty arrays, and empty strings
			if (value === undefined || value === null) return false;
			if (Array.isArray(value) && value.length === 0) return false;
			if (value === '') return false;
			return true;
		})
		.map((f) => {
			const value = filters[f];
			// Handle arrays: serialize as comma-separated values
			if (Array.isArray(value)) {
				return `filters[${f}]=${value.join(',')}`;
			}
			// Handle other values
			return `filters[${f}]=${value}`;
		})
		.join("&");
	const filter = filterFields ? `&${filterFields}` : "";
	return `${pqString}${searchString}${filter}`;
};
